import type { ValueOf } from 'type-fest';
import { z } from 'zod';

const _所属先 = {
  customer: 'customer',
  contract: 'contract',
  ignore: 'ignore',
} as const;
type _所属先 = ValueOf<typeof _所属先>;

const _Type = {
  string: 'string',
  number: 'number',
  date: 'date',
  boolean: 'boolean',
} as const;
type _Type = ValueOf<typeof _Type>;

export namespace _Column {
  export namespace TransformRule {
    const Schema = z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('normalizeKana') }),
      z.object({ kind: z.literal('normalizePhoneNumber'), country: z.literal('JP').optional() }),
      z.object({ kind: z.literal('padStart'), length: z.number(), char: z.string() }),
      z.object({ kind: z.literal('normalizeDate'), format: z.string() }),
    ]);

    export const Construct = (props: unknown): TransformRule => {
      return Schema.parse(props);
    };
  }
  export type TransformRule =
    | { kind: 'normalizeKana' }
    | { kind: 'normalizePhoneNumber'; country?: 'JP' }
    | { kind: 'padStart'; length: number; char: string }
    | { kind: 'normalizeDate'; format: string };

  const LEGACY_TRANSFORM_RULE_KINDS = new Set(['trim', 'preserveLeadingZeros']);

  export type Type = _Type;
  export namespace Type {
    export const FromString = (s: string): Type => {
      const value = _Type[s as keyof typeof _Type];
      if (!value) throw new Error(`Invalid ImportTableDefinition.ColumnType: ${s}`);
      return value;
    };
  }

  export type 所属先 = _所属先;
  export namespace 所属先 {
    export const FromString = (s: string): 所属先 => {
      const value = _所属先[s as keyof typeof _所属先];
      if (!value) throw new Error(`Invalid ImportTableDefinition.Destination: ${s}`);
      return value;
    };
  }

  export type Props = {
    name: string;
    type: string;
    required: boolean;
    description: string | undefined;
    destination: string;
    transformRules: unknown[];
    multiValued?: boolean;
  };
  export const Construct = (props: Props): _Column => {
    const type = Type.FromString(props.type);
    const destination = 所属先.FromString(props.destination);
    const multiValued = props.multiValued === true;
    if (multiValued && destination !== 'customer') {
      throw new Error('multiValued can only be set when destination is customer');
    }
    const transformRules = props.transformRules
      .filter((rule) => {
        if (typeof rule !== 'object' || rule === null || !('kind' in rule)) return true;
        return !LEGACY_TRANSFORM_RULE_KINDS.has(String(rule.kind));
      })
      .map(TransformRule.Construct);
    return {
      name: props.name,
      type,
      required: props.required,
      description: props.description,
      destination,
      transformRules,
      ...(multiValued ? { multiValued: true as const } : {}),
    };
  };
}
export type _Column = {
  name: string;
  type: _Column.Type;
  required: boolean;
  description: string | undefined;
  destination: _Column.所属先;
  transformRules: _Column.TransformRule[];
  multiValued?: true;
};
